import React, { useCallback, useMemo } from 'react';
import { Paper, Stack, Typography } from '@esgian/esgianui';
import { VIEW_BY_TYPE } from '@constants/index';
import { useDispatch, useSelector } from 'react-redux';
import { getDisplayUnit } from '@store/features';
import { getPercentDiff, getValue } from '@helpers';
import {
  getBaselineComparisonSlice,
  setEquipmentFilters
} from '@store/features/filters/BaselineComparisonSlice/BaselineComparisonSlice';
import EquipmentComparisonKpis from '@components/Sections/BaselineComparisonSection/SubSections/EquipmentComparison/EquipmentComparisonKpis';
import { BaselineComparisonChart } from '@components/Charts';
import DataTypeSelect from '../../../../Inputs/DataTypeSelect';
import LookupSelect from '@components/Inputs/LookupSelect';
import { useBaselineComp } from '@hooks/useBaselineComp';
import HourDaySelect from '../../../../Inputs/HourDaySelect';
import { BaselineChartTypeSelect } from '@components/Inputs';
import { useBaselineQueries, useDisplayUnit } from '@hooks';
import { findEquipmentSignal } from '@api/helpers';

function EquipmentComparison({
  allEquipments
}) {
  const displayUnit = useSelector(getDisplayUnit);
  const dispatch = useDispatch();
  const { getEquipmentSeries } = useBaselineComp();
  const { baselineEquipmentQueries } = useBaselineQueries();
  const {
    equipment: { dataType, chartType, viewBy, selectedEquipments }
  } = useSelector(getBaselineComparisonSlice);
  const { valueWithUnit } = useDisplayUnit();

  const handleChange = useCallback((val) => {
    dispatch(setEquipmentFilters(val));
  }, []);
  const mainData = baselineEquipmentQueries[0].data;
  const baselineData = baselineEquipmentQueries[1].data;

  const allEquipmentsOptions = useMemo(() => {
    if (!allEquipments?.length) return [];

    return allEquipments.map(e => ({ value: e.order, key: e.equipmentName, name: e.displayName }));
  }, [allEquipments]);

  const selectedEquipmentsData = useMemo(() => {
    if (selectedEquipments === null || selectedEquipments === undefined)
      return allEquipmentsOptions; // Will be [] if data not loaded, or all items once loaded
    if (selectedEquipments.length === 0 || !allEquipmentsOptions?.length)
      return [];

    return selectedEquipments.filter((sel) => allEquipmentsOptions.find((opt) => opt.value === sel.value));
  }, [selectedEquipments, allEquipmentsOptions]);

  const {
    mainSeries = [],
    baseSeries = [],
    categories = [],
    unit,
    max = 0
  } = useMemo(() => {
    if (!mainData || !baselineData || !selectedEquipmentsData.length) return {};
    return getEquipmentSeries(mainData, baselineData, selectedEquipmentsData);
  }, [displayUnit, mainData, baselineData, selectedEquipmentsData, dataType, viewBy]);

  const loading = baselineEquipmentQueries?.some((item) => item.isFetching);

  const getSumData = (equipmentName) => {
    const mainSignal = findEquipmentSignal(mainData, equipmentName, displayUnit);
    const baseSignal = findEquipmentSignal(baselineData, equipmentName, displayUnit);
    
    if (mainSignal && baseSignal) {
      let mainValues = Object.values(mainSignal.timeSeriesValues || {});
      const mainHasData = mainValues.some(v => v != null && Number(v) !== 0);
      if (!mainHasData) {
        return { value: null, title: null, percent: null, mainRaw: null, baseRaw: null };
      }
      let selectedRigData = mainValues;
      let baseData = Object.values(baseSignal.timeSeriesValues || {});
      selectedRigData = selectedRigData.reduce((a, b) => a + getValue(b), 0);
      baseData = baseData.reduce((a, b) => a + getValue(b), 0);
      let percentageDiff = getPercentDiff(baseData, selectedRigData);
      return {
        ...valueWithUnit(selectedRigData - baseData, displayUnit, true),
        percent: percentageDiff,
        mainRaw: selectedRigData,
        baseRaw: baseData
      };
    }
    return {};
  };

  const { sortedEquipmentsLookup, total, topFourKpis } = useMemo(() => {
    if (!baselineData || !mainData || !allEquipmentsOptions.length) return {};

    const safeAddItem = (equipmentName, header) => {
      const mainSignal = findEquipmentSignal(mainData, equipmentName, displayUnit);
      const baseSignal = findEquipmentSignal(baselineData, equipmentName, displayUnit);
      if (baseSignal && mainSignal) {
        return {
          ...getSumData(equipmentName),
          header
        };
      }
      return null;
    };

    let summedData = [];
    allEquipmentsOptions.forEach((equipment) => {
      summedData.push({ [equipment.key]: safeAddItem(equipment.key, equipment.name) });
    });

    // Filter out null entries
    summedData = summedData.filter(item => Object.values(item)[0] !== null);
    const topKpis = summedData
      .map((item) => ({ ...Object.values(item)[0], percent: parseFloat(Object.values(item)[0].percent ?? 0), key: Object.keys(item)[0] }))
      .sort((a, b) => Math.abs(b.percent) - Math.abs(a.percent));

    const sortedEquipmentsLookup = [...topKpis].map((kpi) => {
      const item = allEquipmentsOptions.find((lookup) => kpi.header === lookup.name);
      return {
        value: item?.value?.toString(),
        ...item
      };
    });
    const allMainNull = summedData.length > 0 && summedData.every(item => Object.values(item)[0]?.mainRaw == null);
    let totalMain = summedData.reduce((a, b) => a + getValue(Object.values(b)[0]?.mainRaw), 0);
    let totalBase = summedData.reduce((a, b) => a + getValue(Object.values(b)[0]?.baseRaw), 0);
    const total = allMainNull
      ? { value: null, title: null, percent: null }
      : {
          ...valueWithUnit(totalMain - totalBase, displayUnit, true),
          percent: getPercentDiff(totalBase, totalMain)
        };
    return { sortedEquipmentsLookup, topFourKpis: topKpis.slice(0, 4), total };
  }, [displayUnit, mainData, baselineData, allEquipmentsOptions]);

  const selectedEquipmentValue = useMemo(() => {
    if (!sortedEquipmentsLookup?.length) 
      return [];
    if (selectedEquipments === null || selectedEquipments === undefined)
      return sortedEquipmentsLookup;
    
    return selectedEquipments.filter((i) => sortedEquipmentsLookup.find((si) => si.value === i.value));
  }, [selectedEquipments, sortedEquipmentsLookup]);

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack justifyContent={'space-between'} direction={'row'}>
          <Typography variant={'h6'}>Equipment comparison</Typography>
        </Stack>
        <Typography variant={'subtitle1'} bold color={'text.secondary'}>
          Comparing to the Baseline
        </Typography>
        <EquipmentComparisonKpis loading={loading} topKpis={topFourKpis} total={total} />
        <Typography variant={'subtitle1'} bold color={'text.secondary'}>
          Comparison over time
        </Typography>
        <BaselineComparisonChart
          dataType={dataType}
          chartType={chartType}
          viewBy={viewBy === VIEW_BY_TYPE.DAILY ? 'day' : 'hour'}
          chartActions={[
            <LookupSelect
              loading={!sortedEquipmentsLookup?.length}
              options={sortedEquipmentsLookup || []}
              label={'Equipment'}
              key={'equipment-select'}
              value={selectedEquipmentValue}
              onChange={(val) => {
                handleChange({ selectedEquipments: val });
              }}
            />,
            <BaselineChartTypeSelect
              key={'baseline-chart-type-select'}
              value={chartType}
              handleChange={(val) => {
                handleChange({ chartType: val });
              }}
            />,
            <DataTypeSelect
              key={'data-type-select'}
              value={dataType}
              onChange={(val) => {
                handleChange({ dataType: val });
              }}
            />,
            <HourDaySelect
              key={'view-by-select'}
              value={viewBy}
              handleChange={(val) => {
                handleChange({ viewBy: val });
              }}
            />
          ]}
          yAxisTitle={unit}
          mainMax={max}
          loading={loading}
          mainSeries={mainSeries}
          baseSeries={baseSeries}
          categories={categories}
        />
      </Stack>
    </Paper>
  );
}

EquipmentComparison.propTypes = {};

EquipmentComparison.defaultProps = {};

export default EquipmentComparison;
